import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { FaBackspace, FaTimesCircle, FaCheckCircle } from "react-icons/fa";

/**
 * POSNumericKeypadModal
 * Touch numeric keypad for quantity/price input (POS tablets)
 *
 * Props:
 *  - isOpen: boolean
 *  - toggle: () => void
 *  - title?: string
 *  - value: string | number
 *  - onConfirm: (valueString: string) => void
 *  - allowDecimal?: boolean
 *  - maxLength?: number
 */
const POSNumericKeypadModal = ({
  isOpen,
  toggle,
  title = "Saisie",
  value,
  onConfirm,
  allowDecimal = true,
  maxLength = 12,
}) => {
  const [localValue, setLocalValue] = useState("");

  useEffect(() => {
    setLocalValue(value === null || value === undefined ? "" : String(value));
  }, [value, isOpen]);

  const keys = useMemo(() => {
    const base = [
      ["7", "8", "9"],
      ["4", "5", "6"],
      ["1", "2", "3"],
    ];
    if (allowDecimal) base.push(["0", ".", "00"]);
    else base.push(["0", "0", "0"]);
    return base;
  }, [allowDecimal]);

  const append = (k) => {
    if (!localValue && (k === "00" || k === ".")) return;

    // Prevent multiple decimals
    if (k === "." && localValue.includes(".")) return;

    const next = (localValue + k).slice(0, maxLength);
    setLocalValue(next);
  };

  const backspace = () => setLocalValue((v) => v.slice(0, -1));

  const clear = () => setLocalValue("");

  const confirm = () => {
    // Normalize: if "." at end => remove
    let v = localValue.trim();
    if (v.endsWith(".")) v = v.slice(0, -1);
    if (!v) v = "0";
    onConfirm(v);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" centered>
      <ModalHeader toggle={toggle}>{title}</ModalHeader>

      <ModalBody>
        {/* Display */}
        <div
          style={{
            border: "2px solid #e5e7eb",
            borderRadius: 14,
            padding: "14px 16px",
            fontSize: 26,
            fontWeight: 800,
            background: "#fff",
            marginBottom: 12,
            minHeight: 62,
            textAlign: "right",
            userSelect: "none",
          }}
        >
          {localValue || <span className="text-muted">0</span>}
        </div>

        {/* Keypad */}
        <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 12 }}>
          {/* Numbers grid */}
          <div style={{ display: "grid", gap: 10 }}>
            {keys.map((row, idx) => (
              <div
                key={idx}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 10,
                }}
              >
                {row.map((k) => (
                  <Button
                    key={k}
                    color="light"
                    className="fw-bold"
                    style={{ height: 64, borderRadius: 16, fontSize: 22 }}
                    onClick={() => append(k)}
                  >
                    {k}
                  </Button>
                ))}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: "grid", gap: 10 }}>
            <Button
              color="warning"
              className="fw-bold"
              style={{ height: 64, borderRadius: 16, fontSize: 18 }}
              onClick={backspace}
            >
              <FaBackspace className="me-2" />
              DEL
            </Button>

            <Button
              color="danger"
              className="fw-bold"
              style={{ height: 64, borderRadius: 16, fontSize: 18 }}
              onClick={clear}
            >
              <FaTimesCircle className="me-2" />
              CLEAR
            </Button>

            <Button
              color="success"
              className="fw-bold"
              style={{ height: 64, borderRadius: 16, fontSize: 18 }}
              onClick={confirm}
            >
              <FaCheckCircle className="me-2" />
              OK
            </Button>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="justify-content-between">
        <Button color="secondary" outline onClick={toggle}>
          Annuler
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default POSNumericKeypadModal;
